import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import type { PostHog } from 'posthog-js';

/**
 * Event / person property bag. Declared locally rather than imported:
 * `posthog-js` re-exports its `Properties` type only from its transitive
 * `@posthog/types` package, and depending on a transitive package's type
 * surface is how a minor bump breaks the build.
 */
type PostHogProperties = Record<string, unknown>;

/**
 * PostHog product analytics — the TRANSPORT. {@link AnalyticsService} is the
 * façade: it owns the consent decision and fans out to both GA4 and this
 * service, so the ~20 existing `analytics.track()` call sites needed no change
 * and there is exactly one source of truth for consent.
 *
 * Consent model (Belgian GDPR / ePrivacy, see `src/assets/legal/cookies.en.md`):
 * PostHog writes to the visitor's device and profiles behaviour, so it needs
 * consent — art. 129 of the Belgian law of 13 June 2005 plus art. 6(1)(a) GDPR.
 * We take the strongest available reading: the library is not even DOWNLOADED
 * until the visitor accepts. Before that, `capture()` and friends drop on the
 * floor. That is why the import is dynamic — declining the banner costs the
 * visitor zero bytes, and there is no way for a misconfiguration to leak a hit.
 *
 * Two consequences worth knowing:
 *  - Events fired before the banner is answered are LOST, not queued. Buffering
 *    them would mean holding behavioural data we have no basis to hold yet.
 *  - Loading is lazy in the bundle sense too: `posthog-js` stays out of the
 *    initial chunk, which already sits close to its budget ceiling.
 *
 * Privacy configuration is deliberately conservative — see `posthogConfig()`.
 */

/** The `posthog.init` options. Exported so the spec can assert on them. */
export function posthogConfig(apiHost: string): Record<string, unknown> {
  return {
    api_host: apiHost,
    ui_host: 'https://eu.posthog.com',
    // Pin the behavioural defaults so a library upgrade cannot silently turn
    // on a new capture mechanism. Everything below is set explicitly anyway.
    defaults: '2025-05-24',
    // AUTOCAPTURE OFF — not negotiable here. Autocapture records the text and
    // attributes of clicked elements; in Proporio that text is tenant names,
    // property addresses and amounts. That is personal data of third parties
    // for which we are a processor on behalf of our agency customers, and it
    // has no business leaving the EU region in an analytics payload. Every
    // event is explicit, from the typed catalogue in analytics-events.ts.
    autocapture: false,
    // Same reasoning, one layer down: no rage-click / dead-click heuristics,
    // no session replay, and any text the SDK does touch is masked.
    disable_session_recording: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Pageviews come from the router hook AnalyticsService already owns, so
    // the SDK must not also emit its own (it would double-count).
    capture_pageview: false,
    capture_pageleave: false,
    // Only reached after an explicit accept, but belt and braces: if the opt-in
    // call below were ever lost, the SDK still sends nothing.
    opt_out_capturing_by_default: true,
    persistence: 'localStorage+cookie',
    // Never let a URL query string become an event property: our routes carry
    // ids, and query strings are where tokens end up.
    property_denylist: ['$current_url', '$initial_current_url', '$referrer', '$initial_referrer'],
  };
}

@Injectable({ providedIn: 'root' })
export class PostHogService {
  private client: PostHog | null = null;
  /** In-flight load, so two rapid `enable()` calls don't import twice. */
  private loading: Promise<PostHog | null> | null = null;

  /** True when this build is configured for PostHog at all. */
  get enabled(): boolean {
    return !!environment.features?.enablePostHog && this.key.length > 0;
  }

  private get key(): string {
    return environment.posthog?.key ?? '';
  }

  private get apiHost(): string {
    return environment.posthog?.apiHost ?? 'https://eu.i.posthog.com';
  }

  /**
   * Load, initialise and opt in. Called only once the visitor has accepted.
   * Idempotent and safe to await concurrently; resolves to null when PostHog
   * is not configured for this build.
   */
  async enable(): Promise<PostHog | null> {
    if (!this.enabled) return null;
    if (this.client) {
      this.client.opt_in_capturing();
      return this.client;
    }
    this.loading ??= this.load();
    return this.loading;
  }

  /**
   * Opt out and forget the visitor. Called on "reject" and when the decision is
   * reset. Never throws — analytics must not break a user flow.
   */
  disable(): void {
    if (!this.client) return;
    try {
      this.client.opt_out_capturing();
      this.client.reset();
    } catch {
      /* analytics must never break a user flow */
    }
  }

  /** Emit an event. No-op until the visitor has accepted. */
  capture(event: string, properties?: PostHogProperties): void {
    this.safely(client => client.capture(event, properties));
  }

  /** Emit a pageview for an SPA navigation, path only (no query string). */
  capturePageView(path: string): void {
    this.safely(client => client.capture('$pageview', { $pathname: path }));
  }

  /**
   * Bind subsequent events to the pseudonymous Firebase uid. Never pass an
   * e-mail or a name — see the person-properties rule in analytics-events.ts.
   */
  identify(uid: string): void {
    this.safely(client => client.identify(uid));
  }

  /** Attach the workspace as a PostHog group, so usage can be read per tenant. */
  group(workspaceId: string): void {
    this.safely(client => client.group('workspace', workspaceId));
  }

  /** Set person properties (workspace_type, plan_tier, locale…). No PII. */
  setPersonProperties(properties: PostHogProperties): void {
    this.safely(client => client.setPersonProperties(properties));
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private async load(): Promise<PostHog | null> {
    try {
      const { default: posthog } = await import('posthog-js');
      posthog.init(this.key, posthogConfig(this.apiHost));
      // dev and production share one PostHog project, so without this every
      // chart would silently mix the two. Registered as a super property, it
      // rides on EVERY event — including $pageview — which is what makes the
      // split filterable after the fact rather than lost.
      posthog.register({ app_environment: environment.environmentName });
      posthog.opt_in_capturing();
      this.client = posthog;
      return posthog;
    } catch {
      // A blocked CDN, an ad blocker, a network failure — none of which is a
      // reason to break the app. We simply stay dark.
      this.loading = null;
      return null;
    }
  }

  /**
   * Run an operation against the client if it exists, swallowing anything it
   * throws. Every public method funnels through here so "not loaded yet" and
   * "PostHog blew up" are both silent no-ops rather than call-site concerns.
   */
  private safely(operation: (client: PostHog) => void): void {
    if (!this.client) return;
    try {
      operation(this.client);
    } catch {
      /* analytics must never break a user flow */
    }
  }
}
